/**
 * SaasYCTemplate — Developer-centric, Stripe/Vercel vibe.
 * Palette: light cream bg + navy text + electric blurple accent
 * Fonts: Outfit (display) + Inter (body)
 */
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string;
  desc: string;
  product: string;
  city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const c = (f: keyof WebsiteCustomizations, fb: string, cu?: WebsiteCustomizations) => cu?.[f] || fb;

const SaasYCTemplate = ({ name, desc, product, customizations, editMode, onCustomize }: Props) => {
  const set = (f: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(f, v);
  const heroTitle = c("heroTitle", name || "Ship faster. Scale safer.", customizations);
  const heroSub = c("heroSubtitle", desc || "The developer platform to move from idea to production in days, not months.", customizations);

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: "#F7F9FC", color: "#0A2540", fontFamily: "'Inter', sans-serif" }}
      data-testid="template-saas-yc"
    >
      {/* Gradient mesh background */}
      <div className="absolute top-0 left-0 right-0 h-[800px] pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #635BFF 0%, transparent 65%)" }} />
        <div className="absolute -top-20 right-20 w-[500px] h-[500px] rounded-full opacity-15" style={{ background: "radial-gradient(circle, #635BFF 0%, transparent 65%)" }} />
      </div>

      {/* Nav */}
      <header className="relative px-6 md:px-16 py-6 flex items-center justify-between">
        <EditableText
          as="div"
          value={name || "Gridline"}
          onChange={set("logoText")}
          editMode={editMode}
          data-field="logoText"
          style={{ fontFamily: "'Outfit', sans-serif", fontSize: "22px", fontWeight: 800, letterSpacing: "-0.02em" }}
        />
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <span className="cursor-pointer hover:opacity-60">Product</span>
          <span className="cursor-pointer hover:opacity-60">Docs</span>
          <span className="cursor-pointer hover:opacity-60">Pricing</span>
          <span className="cursor-pointer hover:opacity-60">Customers</span>
        </nav>
        <div className="flex items-center gap-3">
          <button className="text-sm font-medium hidden md:block hover:opacity-60">Sign in</button>
          <button
            className="px-4 py-2 rounded-full text-white text-sm font-semibold transition-transform hover:scale-105"
            style={{ background: "#635BFF", boxShadow: "0 4px 24px rgba(99,91,255,0.3)" }}
          >
            Get started
          </button>
        </div>
      </header>

      {/* Hero — diagonal cut */}
      <section className="relative px-6 md:px-16 pt-16 md:pt-24 pb-32">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border mb-8" style={{ borderColor: "rgba(99,91,255,0.3)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: "#635BFF" }} />
              <span className="text-xs font-semibold" style={{ color: "#635BFF" }}>v4.2 · Now in beta</span>
            </div>
            <EditableText
              as="h1"
              value={heroTitle}
              onChange={set("heroTitle")}
              editMode={editMode}
              data-field="heroTitle"
              className="mb-6 leading-[0.95]"
              style={{ fontFamily: "'Outfit', sans-serif", fontSize: "clamp(3rem, 6vw, 5.5rem)", fontWeight: 800, letterSpacing: "-0.04em" }}
            />
            <EditableText
              as="p"
              value={heroSub}
              onChange={set("heroSubtitle")}
              editMode={editMode}
              data-field="heroSubtitle"
              multiline
              className="text-lg md:text-xl mb-10 max-w-lg opacity-70"
              style={{ fontWeight: 400, lineHeight: 1.5 }}
            />
            <div className="flex items-center gap-3 flex-wrap">
              <EditableText
                as="button"
                value={c("ctaText", "Start building free", customizations)}
                onChange={set("ctaText")}
                editMode={editMode}
                data-field="ctaText"
                className="px-6 py-3 rounded-full text-white text-sm font-semibold transition-all hover:scale-105"
                style={{ background: "#0A2540", boxShadow: "0 8px 32px rgba(10,37,64,0.2)" }}
              />
              <button className="px-6 py-3 rounded-full border text-sm font-semibold hover:bg-white transition-colors" style={{ borderColor: "rgba(10,37,64,0.2)" }}>
                <span>▶</span> Watch demo · 2min
              </button>
            </div>
            <div className="mt-10 text-xs font-mono opacity-60">$ npm install @{(product || "grid").toLowerCase()}/cli</div>
          </div>
          <div className="relative">
            <div className="rounded-2xl shadow-2xl bg-[#0A2540] overflow-hidden" style={{ boxShadow: "0 32px 80px rgba(10,37,64,0.25)" }}>
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <div className="ml-4 text-xs text-white/40 font-mono">app.ts</div>
              </div>
              <pre className="p-5 text-[13px] font-mono leading-relaxed text-white/90">
<span style={{ color: "#FF8080" }}>import</span> <span style={{ color: "#FFD580" }}>{"{ Grafton }"}</span> <span style={{ color: "#FF8080" }}>from</span> <span style={{ color: "#80FFB4" }}>"@grafton/core"</span>;

<span style={{ color: "#FF8080" }}>const</span> app = <span style={{ color: "#B6B6FF" }}>Grafton</span>.create(&#123;
  tenant: <span style={{ color: "#80FFB4" }}>"acme"</span>,
  region: <span style={{ color: "#80FFB4" }}>"eu-west"</span>,
  plan: <span style={{ color: "#80FFB4" }}>"premium"</span>,
&#125;);

<span style={{ color: "#6C7A8F" }}>// Deployed in 12s ⚡</span>
<span style={{ color: "#FF8080" }}>await</span> app.deploy();
              </pre>
              <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-xs font-mono text-white/50">
                <span>✓ Build success · 12.4s</span>
                <span style={{ color: "#80FFB4" }}>● Live</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento — speed/scale metrics */}
      <section className="relative px-6 md:px-16 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: "#635BFF" }}>Why teams choose us</div>
          <h2 className="mb-12" style={{ fontFamily: "'Outfit', sans-serif", fontSize: "clamp(2rem, 4vw, 3.5rem)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
            Built for <em>velocity</em>. Tuned for scale.
          </h2>

          <div className="grid md:grid-cols-4 grid-rows-2 gap-4 h-auto md:h-[520px]">
            <div className="md:col-span-2 md:row-span-2 rounded-2xl p-8 relative overflow-hidden" style={{ background: "#0A2540", color: "white" }}>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-30 blur-3xl" style={{ background: "#635BFF" }} />
              <div className="relative">
                <div className="text-xs font-semibold opacity-60 mb-4">⚡ DEPLOY SPEED</div>
                <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "80px", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1 }}>
                  <span style={{ color: "#635BFF" }}>12</span>s
                </div>
                <p className="mt-6 opacity-70 max-w-xs">Idea to global production. Edge-first, auto-scaled, zero config.</p>
              </div>
            </div>
            <div className="md:col-span-2 rounded-2xl p-6 bg-white border" style={{ borderColor: "rgba(10,37,64,0.08)" }}>
              <div className="text-xs font-semibold opacity-60 mb-2">UPTIME SLA</div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "48px", fontWeight: 800, letterSpacing: "-0.03em" }}>99.999%</div>
              <p className="text-sm opacity-70 mt-2">Five 9s. Audited yearly.</p>
            </div>
            <div className="md:col-span-1 rounded-2xl p-6" style={{ background: "rgba(99,91,255,0.08)" }}>
              <div className="text-xs font-semibold mb-2" style={{ color: "#635BFF" }}>REGIONS</div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "36px", fontWeight: 800, color: "#0A2540" }}>27</div>
              <p className="text-xs opacity-70 mt-1">Edge nodes worldwide</p>
            </div>
            <div className="md:col-span-1 rounded-2xl p-6 bg-white border" style={{ borderColor: "rgba(10,37,64,0.08)" }}>
              <div className="text-xs font-semibold opacity-60 mb-2">SOC 2</div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "28px", fontWeight: 700 }}>Type II</div>
              <p className="text-xs opacity-70 mt-1">Certified & monitored</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative px-6 md:px-16 py-16 bg-[#0A2540] text-white mt-12">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: "32px", fontWeight: 800, letterSpacing: "-0.03em" }}>
              {name || "Gridline"}
            </div>
            <p className="mt-3 opacity-60 text-sm max-w-xs">{c("footerDesc", "Cloud infrastructure for the builders of tomorrow.", customizations)}</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-50 mb-4">Product</div>
            <div className="space-y-2 text-sm opacity-80">
              <div>Platform</div><div>Pricing</div><div>Changelog</div><div>Docs</div>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-50 mb-4">Company</div>
            <div className="space-y-2 text-sm opacity-80">
              <div>About</div><div>Careers</div>
              <EditableText as="div" value={c("email", "hey@gridline.io", customizations)} onChange={set("email")} editMode={editMode} data-field="email" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-white/10 flex items-center justify-between text-xs opacity-50">
          <span>© 2026 · All systems operational</span>
          <span style={{ color: "#80FFB4" }}>● API: 127ms</span>
        </div>
      </footer>
    </div>
  );
};

export default SaasYCTemplate;
